import React, { useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api, API, getToken } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { TID } from '@/constants/testIds';
import { Send, Sparkles, Bot, Loader, Lightbulb, Brain } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function AICoach() {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [pending, setPending] = useState('');
  const scrollRef = useRef(null);
  // Each language has its own conversation thread so the AI never replies in a stale language.
  const sessionId = `main-${lang}`;

  useEffect(() => {
    setMessages([]); // reset before reload to avoid showing previous-language messages
    api.get(`/ai/messages?session_id=${sessionId}`)
      .then(r => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const msg = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg, id: `u-${Date.now()}` }]);
    setStreaming(true);
    setPending('');

    try {
      const token = getToken();
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ message: msg, session_id: sessionId, language: lang }),
      });
      if (!res.ok || !res.body) throw new Error('stream failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const obj = JSON.parse(line.slice(6));
            if (obj.delta) { full += obj.delta; setPending(full); }
            if (obj.error) toast.error(obj.error);
            if (obj.done) break;
          } catch {}
        }
      }
      setMessages(m => [...m, { role: 'assistant', content: full, id: `a-${Date.now()}` }]);
      setPending('');
    } catch (e) {
      toast.error(t('ai_connection_error'));
    } finally {
      setStreaming(false);
    }
  };

  const QUICK_PROMPTS = [
    { icon: Sparkles, label: t('ai_quick_1_label'), prompt: t('ai_quick_1_prompt') },
    { icon: Brain, label: t('ai_quick_2_label'), prompt: t('ai_quick_2_prompt') },
    { icon: Lightbulb, label: t('ai_quick_3_label'), prompt: t('ai_quick_3_prompt') },
  ];

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_ai')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
            <Bot className="w-10 h-10 text-gold" /> {t('nav_ai')}
          </h1>
          <p className="text-gray-400 mt-1 text-sm">{t('ai_powered_by')}</p>
        </div>
        <Link
          to="/live-game"
          data-testid={TID.aiRecommend}
          className="px-4 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 font-h uppercase tracking-wider text-sm"
        >
          {t('recommend_plays')}
        </Link>
      </div>

      <div className="bg-card-steel border border-gold/30 rounded-lg flex flex-col h-[70vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.length === 0 && !pending && (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gold/40 mx-auto mb-3" />
              <h3 className="font-h uppercase text-2xl mb-2">{t('ai_hero_title')}</h3>
              <p className="text-gray-400 mb-5">{t('ai_hero_subtitle')}</p>
              <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
                {QUICK_PROMPTS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q.prompt)}
                    className="text-left p-3 rounded-md border border-gold/40 hover:bg-gold/10 transition group"
                  >
                    <q.icon className="w-4 h-4 text-gold mb-2" />
                    <div className="text-sm text-white group-hover:text-gold">{q.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-3 whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-navy-deep border border-steel text-white rounded-tr-none'
                  : 'bg-transparent border border-gold/30 text-gray-100 rounded-tl-none'
              }`}>
                {m.role === 'assistant' && <div className="text-[10px] uppercase tracking-widest text-gold mb-1 font-mono">{t('nav_ai')}</div>}
                <div>{m.content}</div>
              </div>
            </div>
          ))}

          {streaming && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-lg rounded-tl-none p-3 bg-transparent border border-gold/30 text-gray-100">
                <div className="text-[10px] uppercase tracking-widest text-gold mb-1 font-mono flex items-center gap-1">
                  <Loader className="w-3 h-3 animate-spin" /> {t('nav_ai')}
                </div>
                <div className="whitespace-pre-wrap">{pending || '...'}</div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-gold/20 p-3 flex gap-2 items-end">
          <textarea
            data-testid={TID.aiInput}
            placeholder={t('chat_placeholder')}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2.5 text-white outline-none resize-none"
          />
          <button
            data-testid={TID.aiSend}
            onClick={send}
            disabled={streaming || !input.trim()}
            className="px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light disabled:opacity-40 transition inline-flex items-center gap-1"
          >
            {streaming ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t('send')}
          </button>
        </div>
      </div>
    </Layout>
  );
}
